import { Icon } from "@mdi/react";
import React, { useEffect, useRef } from "react";

import { mdiPencil } from "@mdi/js";
import iframify from "@noticeable/remarkable-iframify";
import { Button } from "antd";
import { Remarkable } from "remarkable";

import { Tooltip } from "components/base";
import DocumentationTag from "components/user-documentation/DocumentationTag";
import { DOWNLOAD_URL } from "components/user-documentation/constants";

import VideoPlayer from "../../VideoPlayer";
import styles from "../../styles/notificationViews.module.scss";
import { NotificationItem } from "../../types";

interface NotificationPostViewProps {
  notification?: NotificationItem;
  postId?: string;
  onEdit?: () => void;
  canEdit: boolean;
}

const NotificationPostView: React.FC<NotificationPostViewProps> = ({
  notification,
  onEdit,
  canEdit
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset scroll position when notification changes
  useEffect(() => {
    if (containerRef.current && notification) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [notification?.id]); // Dependency on notification ID to trigger when switching posts

  const md = new Remarkable({
    html: true
  });
  md.use(iframify);
  md.use(VideoPlayer);

  // Fix image URLs in old posts that may have broken or outdated URLs
  const fixImageUrls = (content: string): string => {
    if (!content) return content;

    // Pattern to match markdown images: ![alt text](url)
    // This handles various URL patterns that might exist in old posts
    return content.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, altText, url) => {
      // If the URL already uses the current DOWNLOAD_URL, leave it as is
      if (url.includes(DOWNLOAD_URL)) {
        return match;
      }

      // Extract file ID from various possible old URL patterns
      let fileId = null;

      // Pattern 1: URLs ending with /download/{fileId} (most common in old posts)
      const downloadMatch = url.match(/\/download\/([a-zA-Z0-9]+)$/);
      if (downloadMatch) {
        fileId = downloadMatch[1];
      }

      // Pattern 2: Direct file IDs (in case some old posts stored just the ID)
      const directIdMatch = url.match(/^[a-zA-Z0-9]{24}$/); // MongoDB ObjectId pattern
      if (directIdMatch && !fileId) {
        fileId = url;
      }

      // Pattern 3: Any URL with a file ID in the path
      const pathIdMatch = url.match(/([a-zA-Z0-9]{24})/);
      if (pathIdMatch && !fileId) {
        fileId = pathIdMatch[1];
      }

      // If we found a file ID, construct the proper URL
      if (fileId) {
        return `![${altText}](${DOWNLOAD_URL}/${fileId})`;
      }

      // If we can't extract a file ID, return the original (might be an external image)
      return match;
    });
  };

  const getMarkup = () => {
    if (!notification?.content) return { __html: "" };

    // Fix any broken image URLs in old posts before rendering
    const processedContent = fixImageUrls(notification.content);

    return { __html: md.render(processedContent) };
  };

  const getDate = (date: string) => {
    const dateObj = new Date(Date.parse(date));
    const options = {
      month: "short" as const,
      day: "numeric" as const,
      year: "numeric" as const
    };
    return new Intl.DateTimeFormat("en-US", options).format(dateObj);
  };

  if (!notification) {
    return (
      <div className={styles.notificationPostView} ref={containerRef}>
        <div className={styles.noPostMessage}>Select a post to view</div>
      </div>
    );
  }

  return (
    <div className={styles.notificationPostView} ref={containerRef}>
      <div className={styles.postHeader}>
        <h2>{notification.title}</h2>

        {canEdit && onEdit && (
          <Tooltip title="Edit Post">
            <Button type="text" onClick={onEdit} className={styles.editButton}>
              <Icon path={mdiPencil} size="2.75rem" />
            </Button>
          </Tooltip>
        )}
      </div>

      <h5>{notification.date && getDate(notification.date)}</h5>

      <div className={styles.tagsContainer}>
        {notification.tag &&
          notification.tag.map((tag) => <DocumentationTag key={tag} value={tag} />)}
      </div>

      <div dangerouslySetInnerHTML={getMarkup()} />
    </div>
  );
};

export default NotificationPostView;
